import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAccountSetup from './useAccountSetup';
import useUserNotificationStates from './useUserNotificationStates';
import { getAllCardDefs, getReplacementCard, getTopCards, orderCardDefs, resolveCards } from './useCTAs.utils';

/**
 * useCTAs(currentUser, maxCards)
 *
 * Returns up to `maxCards` CTACard prop objects for the home screen, plus
 * the bits StreamlinedLayout needs to animate them in/out with Headless UI's
 * Transition component.
 *
 * Card selection: the backend (userNotificationStates) decides which cards
 * apply to the user and computes their status. The UI decides ordering:
 * the per-segment order for currentUser.ctaSegment (see orderCardDefs), or
 * the getAllCardDefs() master order when there is no segment — we take up to
 * `maxCards` cards (invisible/blocked cards are skipped when filling gaps).
 * How many cards to show is a layout concern, so `maxCards` is supplied by
 * the caller (StreamlinedLayout) rather than hard-coded here.
 *
 * On a user's first read, the backend may take a few hundred ms to compute
 * the cards, so the home screen blocks (isLoading) until the data arrives —
 * but only up to a short timeout. If the read takes too long or fails, no
 * CTA cards are shown.
 *
 * A completed card needs special treatment: rather than being marked shown
 * as soon as it appears (like every other card), StreamlinedLayout waits a
 * short while after the cards have loaded and then reports it as shown on
 * its own — see onCardShown below. This hook only needs to react to that:
 * as soon as a completed card is reported shown, it should exit immediately
 * (exitingId) so it can be removed and, if a replacement is available, one
 * is pulled in once the layout confirms (via onCardExited) that the exit
 * transition has actually finished in the DOM.
 *
 * A card can also arrive already completed on the very first load (e.g. it
 * was completed elsewhere before the user ever visited this screen) while
 * still having a shownAt that predates its completion — i.e. the user was
 * shown the card before it completed, but never got to see it complete. Such
 * a card is still owed one more appearance before it exits, so the initial
 * card selection includes any such pending-exit card (needsExitAnimation)
 * even though it would otherwise be skipped as already-successful.
 *
 * On every mount, this hook itself forces one fresh read of the cards (see
 * isMountRefetchPending below) — e.g. the user may have completed a card
 * elsewhere and then navigated back to this screen, and cached (stale) data
 * must not be used to decide the initial card order/selection. `refetch` is
 * also passed straight through from useUserNotificationStates so callers
 * (e.g. StreamlinedLayout) can force additional manual refreshes if needed.
 */
const MAX_WAIT_MS = 2500;
const DEFAULT_MAX_CARDS = 3;

export default function useCTAs(currentUser, maxCards = DEFAULT_MAX_CARDS) {
  const accountSetup = useAccountSetup(currentUser);
  const hasUser = !!currentUser?.data?.id;
  // Backend-computed segment; when missing, cards keep the master order
  const ctaSegment = currentUser?.data?.ctaSegment ?? null;

  const {
    statesByKey,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    markShown,
    refetch,
  } = useUserNotificationStates({ enabled: hasUser });

  const isFetchingStates = hasUser && isLoading;
  // A background refetch (e.g. triggered by invalidating the cache) does not
  // flip isLoading back to true since data is already cached, but it should
  // still be treated as "loading" so a displayed card that becomes blocked
  // as a result is swapped out (see below) instead of acting on stale data.
  const isRefetchingStates = hasUser && isFetching;

  // Cached (stale) data can be served immediately on mount (e.g. when the
  // user navigates away and back to this screen) while a fresh read is still
  // in flight. If the initial card selection below were to run against that
  // stale snapshot, a card that was just completed elsewhere could be locked
  // into its old display slot instead of being recognized as needing an exit
  // animation. So force a fresh read once per mount and hold off computing
  // any cards until it settles (bounded by the same MAX_WAIT_MS timeout used
  // for the very first load).
  const hasTriggeredMountRefetchRef = useRef(false);
  const [isMountRefetchPending, setIsMountRefetchPending] = useState(false);

  useEffect(() => {
    if (!hasUser || hasTriggeredMountRefetchRef.current) return;
    hasTriggeredMountRefetchRef.current = true;

    if (typeof refetch !== 'function') return;

    setIsMountRefetchPending(true);
    Promise.resolve(refetch()).finally(() => setIsMountRefetchPending(false));
  }, [hasUser, refetch]);

  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!isFetchingStates && !isMountRefetchPending) return undefined;
    const timer = setTimeout(() => setTimedOut(true), MAX_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isFetchingStates, isMountRefetchPending]);

  // Block until we have data, unless we've waited too long or the request failed.
  const isBlocking = (isFetchingStates || isMountRefetchPending) && !timedOut;
  const giveUp = isError || ((isFetchingStates || isMountRefetchPending) && timedOut);

  const resolvedCards = useMemo(() => {
    if (accountSetup.isFetching || isBlocking || giveUp || !isSuccess) return [];

    const cardDefs = orderCardDefs(getAllCardDefs(accountSetup), ctaSegment);
    return resolveCards(cardDefs, statesByKey);
  }, [
    accountSetup.isFetching,
    accountSetup.isFeedbackCTA,
    accountSetup.isFellowshipCTA,
    ctaSegment,
    isBlocking,
    giveUp,
    isSuccess,
    statesByKey,
  ]);

  // The ids of the (up to maxCards) cards currently displayed. Initialized
  // once resolvedCards become available, then only ever changed when a card
  // exits and is replaced — or when a background refetch (see below)
  // reveals that a displayed card is now blocked and must be swapped out
  // immediately for a better one.
  const [displayedIds, setDisplayedIds] = useState(null);

  useEffect(() => {
    if (displayedIds !== null || resolvedCards.length === 0) return;

    // Cards that are already completed but still owe the user one more
    // appearance (e.g. completed before this load) must be included up
    // front, since getTopCards would otherwise skip them as already
    // successful and they'd never get a chance to be shown and exit.
    const pendingExitIds = resolvedCards
      .filter((c) => c.needsExit)
      .map((c) => c.id);

    // Cap up front: the top-up below only refuses to *add* past maxCards,
    // it can't shorten an already-oversized list.
    const initialIds = pendingExitIds.slice(0, maxCards);
    getTopCards(resolvedCards, true, maxCards).forEach((c) => {
      if (initialIds.length >= maxCards || initialIds.includes(c.id)) return;
      initialIds.push(c.id);
    });

    setDisplayedIds(initialIds);
  }, [resolvedCards, displayedIds, statesByKey, maxCards]);

  // The id of the card StreamlinedLayout should currently be transitioning
  // out, or null if none. Only one card exits at a time.
  const [exitingId, setExitingId] = useState(null);

  // Ids that have already been fully displayed-and-exited. getReplacementCard
  // no longer skips completed cards (a completed card can legitimately be
  // pulled in as a replacement — see its docstring), so once a card has
  // finished its own show-then-exit cycle it must be remembered here,
  // otherwise it would immediately be eligible to replace itself (it's still
  // "success" and, the instant it's removed, no longer in displayedIds).
  const exitedIdsRef = useRef(new Set());

  // A background refetch (e.g. triggered by manually invalidating the
  // cache, or by the query becoming stale) fetches fresh data without
  // flipping isLoading, so a displayed card that becomes blocked as a
  // result (e.g. cta:setup-profile going back to "started" blocks the
  // cards that were displayed in its place) would otherwise keep occupying
  // a slot forever. Once such a refetch completes, immediately drop any
  // now-blocked displayed card and pull in the next best replacement.
  // Cards that merely complete are left alone here — they still exit via
  // the usual onCardShown flow below.
  //
  // The mount-triggered refetch above (isMountRefetchPending) must be
  // folded into this "still refreshing" signal too: react-query's own
  // isFetching can flip back to false a render or two before our
  // isMountRefetchPending state catches up (its `refetch().finally(...)`
  // callback settles slightly later), and resolvedCards is still forced to
  // `[]` while isMountRefetchPending is true (see isBlocking above). If
  // this effect only watched isRefetchingStates, that in-between render
  // would see resolvedCards as empty and treat every displayed card as "no
  // longer good", wiping displayedIds down to nothing with no replacements
  // — and since displayedIds would then be a non-null empty array, the
  // initial-selection effect above would never run again, so no cards
  // would ever be shown again after that mount. Waiting for both flags to
  // settle avoids ever running this against a resolvedCards snapshot that
  // is only empty because of blocking, not because of real data.
  const isRefreshingCards = isRefetchingStates || isMountRefetchPending;
  const prevIsRefreshingCardsRef = useRef(isRefreshingCards);

  useEffect(() => {
    if (prevIsRefreshingCardsRef.current && !isRefreshingCards) {
      setDisplayedIds((prev) => {
        if (!prev) return prev;

        const stillGood = prev.filter((id) => {
          const card = resolvedCards.find((c) => c.id === id);
          return card && !card.disabled;
        });

        const next = [...stillGood];
        for (let i = 0; i < prev.length - stillGood.length; i += 1) {
          const replacement = getReplacementCard(
            resolvedCards,
            [...next, ...exitedIdsRef.current],
          );
          if (!replacement) break;
          next.push(replacement.id);
        }

        return next;
      });
    }
    prevIsRefreshingCardsRef.current = isRefreshingCards;
  }, [isRefreshingCards, resolvedCards]);

  // onCardExited (wired to the leave transition's afterLeave) is normally the
  // only thing that clears exitingId — but afterLeave never fires if React
  // unmounts the Transition mid-animation, which happens when the refetch
  // reconciliation above drops the exiting card from displayedIds inside its
  // ~150ms leave window. exitingId would then stay set for the rest of the
  // mount, permanently blocking every later card from exiting. Clear it
  // defensively whenever the card it names is no longer displayed.
  useEffect(() => {
    if (exitingId && displayedIds && !displayedIds.includes(exitingId)) {
      exitedIdsRef.current.add(exitingId);
      setExitingId(null);
    }
  }, [exitingId, displayedIds]);

  const isCtaLoading = isBlocking || accountSetup.isFetching || isFetchingStates;

  const ctas = useMemo(() => {
    if (!displayedIds) return [];

    return displayedIds
      .map((id) => resolvedCards.find((c) => c.id === id))
      .filter(Boolean);
  }, [displayedIds, resolvedCards]);

  // Record shownAt for each of the displayed cards, once per card, the
  // first time StreamlinedLayout notifies us that it has actually been
  // shown to the user. For a non-completed card, that's as soon as its
  // enter transition completes. A completed card, however, isn't reported
  // shown until StreamlinedLayout's own delayed timer fires (so the user
  // gets a moment to see it before it's whisked away) — and as soon as
  // that happens, it should exit immediately rather than lingering.
  const markedShownRef = useRef(new Set());

  const onCardShown = useCallback((id) => {
    const state = statesByKey[id];
    if (!state) return;

    if (state.status === 'completed') {
      // A completed card only needs to be shown once — as soon as
      // StreamlinedLayout reports it, kick off its exit so it can be
      // removed from the list (see onCardExited). Only one card exits at a
      // time, so leave an already-exiting card alone.
      setExitingId((prev) => prev ?? id);
      return;
    }

    if (state.shownAt || markedShownRef.current.has(id)) return;

    markedShownRef.current.add(id);
    markShown(id).catch(() => {
      // Allow a retry on a future render if the request failed.
      markedShownRef.current.delete(id);
    });
  }, [statesByKey, markShown]);

  // Called by StreamlinedLayout once the exiting card's leave transition has
  // actually finished in the DOM: remove it, pull in a replacement if one is
  // available, and refresh shownAt on the backend so this card won't be
  // flagged for exit again.
  const onCardExited = useCallback((id) => {
    exitedIdsRef.current.add(id);

    setDisplayedIds((prev) => {
      if (!prev) return prev;
      const remaining = prev.filter((cardId) => cardId !== id);
      const excludeIds = [...remaining, ...exitedIdsRef.current];
      const replacement = getReplacementCard(resolvedCards, excludeIds);
      return replacement ? [...remaining, replacement.id] : remaining;
    });

    markShown(id).catch(() => {});
    setExitingId(null);
  }, [resolvedCards, markShown]);

  return {
    ctas,
    isLoading: isCtaLoading,
    exitingId,
    onCardShown,
    onCardExited,
    refetch,
  };
}
